import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Copy, Sparkles } from "lucide-react";
import { API } from "@/App";
import { waLink } from "@/data/content";

export default function Admin() {
  const location = useLocation();
  const [admin, setAdmin] = useState(location.state?.user || null);
  const [form, setForm] = useState({ email: "Bimabachatbazar@gmail.com", password: "" });
  const [leads, setLeads] = useState([]);
  const [payments, setPayments] = useState([]);
  const [tab, setTab] = useState("leads");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [drafts, setDrafts] = useState({});

  const loadLeads = () => axios.get(`${API}/leads`, { withCredentials: true }).then(({ data }) => setLeads(data)).catch(() => {});
  const loadPayments = () => axios.get(`${API}/payments`, { withCredentials: true }).then(({ data }) => setPayments(data)).catch(() => {});

  useEffect(() => {
    if (!admin) {
      axios.get(`${API}/auth/me`, { withCredentials: true }).then(({ data }) => setAdmin(data)).catch(() => setAdmin(null));
      return;
    }
    loadLeads();
    loadPayments();
  }, [admin]);

  const login = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, form, { withCredentials: true });
      setAdmin(data);
    } catch (e) {
      setError(e.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const updateStatus = async (id, status) => {
    await axios.patch(`${API}/leads/${id}`, { status }, { withCredentials: true });
    loadLeads();
  };

  const draftFollowup = async (lead) => {
    setDrafts((d) => ({ ...d, [lead.id]: "loading" }));
    try {
      const { data } = await axios.post(`${API}/admin/draft-followup`, { lead_id: lead.id }, { withCredentials: true });
      setDrafts((d) => ({ ...d, [lead.id]: data.draft }));
    } catch {
      setDrafts((d) => ({ ...d, [lead.id]: "" }));
      toast.error("AI draft nahi ban paya.");
    }
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    setAdmin(null);
  };

  if (!admin) {
    return (
      <div className="dashboard-shell">
        <div className="dashboard-login">
          <Link to="/" className="dashboard-back" data-testid="dashboard-back-button">← Back to website</Link>
          <div className="brand"><span className="brand-mark">BB</span><span className="brand-text"><strong>BIMA BACHAT</strong><small>BAZAR</small></span></div>
          <div className="eyebrow blue">Private advisor area</div>
          <h1>Welcome back.</h1>
          <p>Sign in to follow up with enquiries and payments.</p>
          <form onSubmit={login} data-testid="advisor-login-form">
            <label>Email<input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="advisor-email-input" /></label>
            <label>Password<input type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="advisor-password-input" /></label>
            {error && <div className="dashboard-error" data-testid="advisor-login-error">{error}</div>}
            <button className="btn btn-primary btn-block" disabled={loading} data-testid="advisor-login-button">{loading ? "Signing in…" : "Sign in"}</button>
          </form>
          <div className="divider"><span>or</span></div>
          <button className="btn btn-ghost btn-block" onClick={googleLogin} data-testid="advisor-google-login-button">Sign in with Google</button>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-shell">
      <div className="dashboard-top">
        <div className="brand"><span className="brand-mark">BB</span><span className="brand-text"><strong>BIMA BACHAT</strong><small>BAZAR</small></span></div>
        <div className="dashboard-actions">
          <span>{admin.email}</span>
          <Link to="/" data-testid="dashboard-home-button">Website</Link>
          <button onClick={logout} data-testid="advisor-logout-button">Log out</button>
        </div>
      </div>
      <div className="dashboard-content">
        <div className="eyebrow blue">Advisor dashboard</div>
        <h1>Enquiries & Payments</h1>
        <div className="admin-tabs" data-testid="admin-tabs">
          <button className={tab === "leads" ? "active" : ""} onClick={() => setTab("leads")} data-testid="admin-tab-leads">Leads ({leads.length})</button>
          <button className={tab === "payments" ? "active" : ""} onClick={() => setTab("payments")} data-testid="admin-tab-payments">Payments ({payments.length})</button>
        </div>
        {tab === "leads" && (
          <div className="lead-table" data-testid="lead-dashboard-table">
            <div className="lead-table-head"><span>Contact</span><span>Interested in</span><span>Follow-up</span><span>Status</span></div>
            {leads.length === 0 && <div className="empty-leads" data-testid="empty-leads">No enquiries yet.</div>}
            {leads.map((lead) => (
              <div className="lead-table-row" key={lead.id} data-testid={`lead-row-${lead.id}`}>
                <div>
                  <strong>{lead.name}</strong>
                  <small>{lead.phone}{lead.email ? ` • ${lead.email}` : ""}</small>
                  <small>{lead.message || "No message"}{(lead.documents || []).length ? ` • ${lead.documents.length} doc(s)` : ""}</small>
                </div>
                <span>{(lead.interests && lead.interests.length ? lead.interests : [lead.service]).join(", ")}</span>
                <div className="lead-followup">
                  <a href={waLink(`Namaste ${lead.name} ji, Bima Bachat Bazar se Utkarsh Gupta bol raha hoon. Aapki enquiry ke baare mein baat karni thi.`)} target="_blank" rel="noopener noreferrer" className="btn btn-whatsapp btn-xs" data-testid={`lead-whatsapp-${lead.id}`}>WhatsApp</a>
                  <button className="btn btn-ghost btn-xs" onClick={() => draftFollowup(lead)} data-testid={`lead-draft-${lead.id}`}><Sparkles size={13} /> AI Draft</button>
                  {drafts[lead.id] && drafts[lead.id] !== "loading" && (
                    <div className="draft-box" data-testid={`lead-draft-text-${lead.id}`}>
                      <p>{drafts[lead.id]}</p>
                      <button className="btn btn-ghost btn-xs" onClick={() => { navigator.clipboard.writeText(drafts[lead.id]); toast.success("Copied!"); }} data-testid={`lead-draft-copy-${lead.id}`}><Copy size={12} /> Copy</button>
                    </div>
                  )}
                  {drafts[lead.id] === "loading" && <small>AI likh raha hai…</small>}
                </div>
                <select value={lead.status || "new"} onChange={(e) => updateStatus(lead.id, e.target.value)} data-testid={`lead-status-${lead.id}`}>
                  <option value="new">New</option>
                  <option value="contacted">Contacted</option>
                  <option value="closed">Closed</option>
                </select>
              </div>
            ))}
          </div>
        )}
        {tab === "payments" && (
          <div className="lead-table" data-testid="payments-table">
            <div className="lead-table-head"><span>Customer</span><span>Service</span><span>Amount</span><span>Status</span></div>
            {payments.length === 0 && <div className="empty-leads" data-testid="empty-payments">No payments yet.</div>}
            {payments.map((p) => (
              <div className="lead-table-row" key={p.id} data-testid={`payment-row-${p.id}`}>
                <div><strong>{p.name}</strong><small>{p.phone}</small></div>
                <span>{p.service_name}</span>
                <span className="mono">₹{p.amount}</span>
                <span className={p.status === "paid" ? "ret pos" : "ret neg"}>{p.status}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
